import type { UseAsyncListReturn } from '@ark-ui/react/collection';
import { useEffect, useMemo, useRef, useState } from 'react';

import { createRequestLifecycle, type RequestLifecycle } from '../requestLifecycle';
import {
  canSearchListLoadMore,
  createSearchListExecutionState,
  resolveSearchListLoadMoreCursor,
  resolveSearchListLoadMoreError,
  resolveSearchListLoadMoreSuccess,
  resolveSearchListReloadError,
  resolveSearchListReloadSuccess,
  resetSearchListExecutionForSortChange,
  startSearchListLoadMore,
  startSearchListReload,
} from './executionState';
import type {
  SearchListExecutionState,
  UseSearchListStateOptions,
  UseSearchListStateResult,
} from './types';
import { sanitizeSortValue } from './urlState';

export const useSearchListState = <
  Item,
  FilterName extends string,
  ListKey extends string,
  SortValue extends string,
  Cursor,
>(
  options: UseSearchListStateOptions<Item, FilterName, ListKey, SortValue, Cursor>
): UseSearchListStateResult<Item, FilterName, SortValue, Cursor> => {
  type ListState = SearchListExecutionState<Item, FilterName, SortValue, Cursor>;
  const defaultSort = options.defaultSort ?? null;
  const definition = useMemo(
    () => ({
      pageSize: options.pageSize,
      defaultSort,
    }),
    [defaultSort, options.pageSize]
  );
  const [state, setState] = useState<ListState>(
    () => createSearchListExecutionState(definition, options.initialFacetState) as ListState
  );

  const stateRef = useRef(state);
  const criteriaRef = useRef(options.criteria);
  // Lazy init: `useRef(create...())` would allocate a discarded instance on every render.
  const lifecycleRef = useRef<RequestLifecycle | null>(null);
  if (lifecycleRef.current === null) {
    lifecycleRef.current = createRequestLifecycle();
  }
  const lifecycle = lifecycleRef.current;

  stateRef.current = state;
  criteriaRef.current = options.criteria;

  const abort = () => {
    lifecycle.abort();
  };

  const runReload = (nextState: typeof stateRef.current, criteria = criteriaRef.current) => {
    const handle = lifecycle.startRequest();

    setState(startSearchListReload(nextState));

    void options
      .fetcher({
        listKey: options.listKey,
        query: criteria.query,
        filters: criteria.filters,
        sort: nextState.sort,
        take: nextState.take,
        signal: handle.controller.signal,
      })
      .then((result) => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setState((currentState) =>
          resolveSearchListReloadSuccess(currentState, definition, result)
        );
      })
      .catch((error) => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setState((currentState) => resolveSearchListReloadError(currentState, error));
      });
  };

  const loadMore = () => {
    const currentState = stateRef.current;

    if (!canSearchListLoadMore(currentState)) {
      return;
    }

    const handle = lifecycle.startRequest();
    const nextState = startSearchListLoadMore(currentState, definition);
    const cursor = resolveSearchListLoadMoreCursor(currentState, definition);

    setState(nextState);

    void options
      .fetcher({
        listKey: options.listKey,
        query: criteriaRef.current.query,
        filters: criteriaRef.current.filters,
        sort: currentState.sort,
        take: definition.pageSize,
        cursor,
        signal: handle.controller.signal,
      })
      .then((result) => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setState((currentListState) =>
          resolveSearchListLoadMoreSuccess(currentListState, definition, result)
        );
      })
      .catch((error) => {
        if (lifecycle.isStale(handle)) {
          return;
        }

        setState((currentListState) =>
          resolveSearchListLoadMoreError(currentListState, definition, error)
        );
      });
  };

  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    runReload(stateRef.current, criteriaRef.current);
  }, [options.enabled, options.serializedCriteria, state.sort]);

  useEffect(() => {
    return () => {
      abort();
    };
  }, []);

  const applyUrlState = ({
    take: nextTake,
    sort: nextSort,
  }: {
    take: number;
    sort: SortValue | null;
  }) => {
    setState((currentState) => ({
      ...currentState,
      take: nextTake,
      sort: nextSort,
      hasLoaded: false,
    }));
  };

  const setSort = (nextSort: SortValue | null) => {
    const sanitizedSort = sanitizeSortValue(
      nextSort ?? undefined,
      options.sortOptions,
      defaultSort
    );

    setState((currentState) =>
      resetSearchListExecutionForSortChange(currentState, definition, sanitizedSort)
    );
  };

  const resetSort = () => {
    setState((currentState) =>
      resetSearchListExecutionForSortChange(currentState, definition, defaultSort)
    );
  };

  const resetTake = () => {
    setState((currentState) => ({
      ...currentState,
      take: definition.pageSize,
      hasLoaded: false,
      error: undefined,
      nextCursor: undefined,
    }));
  };

  // Keep the historical `searchList` field stable for existing consumers even though the
  // execution layer now runs on our shared list-state core instead of Ark's implementation.
  const searchList = {
    items: state.items,
    filterText: '',
    cursor: state.nextCursor,
    sortDescriptor: undefined,
    loading: state.loading,
    sorting: false,
    empty: state.items.length === 0,
    hasMore: state.hasMore,
    error: state.error,
    abort,
    reload: () => runReload(stateRef.current),
    loadMore,
    sort: () => {},
    setFilterText: () => {},
    clearFilter: () => {},
  } as UseAsyncListReturn<Item, Cursor>;

  return {
    searchList,
    items: state.items,
    loading: state.loading,
    hasLoaded: state.hasLoaded,
    error: state.error,
    hasMore: state.hasMore,
    totalCount: state.totalCount,
    take: state.take,
    sort: state.sort,
    sortOptions: options.sortOptions ?? [],
    setSort,
    resetSort,
    resetTake,
    loadMore,
    applyUrlState,
    facetState: state.facetState,
  };
};
